/**
 * MerchantAvatar — Merchant logo with 3-tier fallback:
 *   1. Plaid logo_url (best — 100×100 PNG from Plaid CDN)
 *   2. Google Favicon heuristic (decent — works for major brands)
 *   3. CategoryIcon (generic colored circle)
 */

import { useState } from 'react';
import { T } from '@/styles/tokens';
import { CategoryIcon } from '@/components/CategoryIcon';

// ── Known merchant → domain mappings ────────────────────────────

const MERCHANT_DOMAINS: Record<string, string> = {
  'blick art materials': 'dickblick.com',
  'dick blick': 'dickblick.com',
  "jerry's artarama": 'jerrysartarama.com',
  'jerrys artarama': 'jerrysartarama.com',
  'utrecht': 'utrechtart.com',
  'golden artist colors': 'goldenpaints.com',
  'winsor & newton': 'winsornewton.com',
  'winsor newton': 'winsornewton.com',
  'gamblin': 'gamblincolors.com',
  'daniel smith': 'danielsmith.com',
  'michaels': 'michaels.com',
  'hobby lobby': 'hobbylobby.com',
  'joann': 'joann.com',
  'amazon': 'amazon.com',
  'paypal': 'paypal.com',
  'venmo': 'venmo.com',
  'square': 'squareup.com',
  'stripe': 'stripe.com',
  'etsy': 'etsy.com',
  'shopify': 'shopify.com',
  'uber': 'uber.com',
  'lyft': 'lyft.com',
  'starbucks': 'starbucks.com',
  'walmart': 'walmart.com',
  'target': 'target.com',
  'united airlines': 'united.com',
  'american airlines': 'aa.com',
  'delta': 'delta.com',
  'southwest': 'southwest.com',
  'costco': 'costco.com',
  'home depot': 'homedepot.com',
  'lowes': 'lowes.com',
  "lowe's": 'lowes.com',
  'whole foods': 'wholefoodsmarket.com',
  'trader joes': 'traderjoes.com',
  "trader joe's": 'traderjoes.com',
  'netflix': 'netflix.com',
  'spotify': 'spotify.com',
  'apple': 'apple.com',
  'google': 'google.com',
  'doordash': 'doordash.com',
  'grubhub': 'grubhub.com',
  'chipotle': 'chipotle.com',
  'mcdonalds': 'mcdonalds.com',
  "mcdonald's": 'mcdonalds.com',
  'chick-fil-a': 'chick-fil-a.com',
  'usps': 'usps.com',
  'fedex': 'fedex.com',
  'ups': 'ups.com',
};

function merchantToFaviconUrl(merchantName: string): string | null {
  if (!merchantName) return null;
  const cleaned = merchantName.toLowerCase()
    .replace(/\s+(inc|llc|corp|co|ltd)\.?$/i, '')
    .trim();

  // Exact match
  const known = MERCHANT_DOMAINS[cleaned];
  if (known) return `https://www.google.com/s2/favicons?domain=${known}&sz=128`;

  // Partial match
  for (const [key, domain] of Object.entries(MERCHANT_DOMAINS)) {
    if (cleaned.includes(key)) return `https://www.google.com/s2/favicons?domain=${domain}&sz=128`;
  }

  // Heuristic: first word + .com (only for longer names to avoid false positives)
  const firstWord = cleaned.split(/\s+/)[0];
  if (firstWord && firstWord.length >= 4) {
    return `https://www.google.com/s2/favicons?domain=${firstWord}.com&sz=128`;
  }

  return null;
}

// ── Component ───────────────────────────────────────────────────

interface MerchantAvatarProps {
  merchantName: string;
  logoUrl?: string | null;
  category: string;
  size?: number;
}

export function MerchantAvatar({ merchantName, logoUrl, category, size = 28 }: MerchantAvatarProps) {
  const [plaidFailed, setPlaidFailed] = useState(false);
  const [faviconFailed, setFaviconFailed] = useState(false);

  const effectiveLogoUrl = (!logoUrl || plaidFailed) ? null : logoUrl;
  const faviconUrl = merchantToFaviconUrl(merchantName);

  // Tier 1: Plaid logo_url
  if (effectiveLogoUrl) {
    return (
      <img
        src={effectiveLogoUrl}
        alt=""
        onError={() => setPlaidFailed(true)}
        style={{
          width: size,
          height: size,
          borderRadius: size / 2,
          objectFit: 'cover',
          backgroundColor: T.white,
          border: `1px solid ${T.borderLight}`,
        }}
      />
    );
  }

  // Tier 2: Google Favicon heuristic
  if (faviconUrl && !faviconFailed) {
    return (
      <img
        src={faviconUrl}
        alt=""
        onError={() => setFaviconFailed(true)}
        style={{
          width: size,
          height: size,
          borderRadius: size / 2,
          objectFit: 'contain',
          backgroundColor: T.white,
          border: `1px solid ${T.borderLight}`,
        }}
      />
    );
  }

  // Tier 3: CategoryIcon fallback
  return <CategoryIcon category={category} size={size} />;
}
